
import React from 'react';

import { translate } from '../../base/i18n';
import { CircularLabel } from '../../base/label';
import { MEDIA_TYPE } from '../../base/media';
import { connect } from '../../base/redux';
import Tooltip from '../../base/tooltip/components/Tooltip';
import { getTrackByMediaTypeAndParticipant } from '../../base/tracks';
import { mapResolutionToTranslationsKeys } from '../functions';

import AbstractVideoQualityLabel, {
    type Props as AbstractProps,
    _abstractMapStateToProps
} from './AbstractVideoQualityLabel';

type Props = AbstractProps & {

  /**
   * The message to show within the label.
   */
  _labelKey: string,

  /**
   * The message to show within the label's tooltip.
   */
  _tooltipKey: string,

  /**
   * The redux representation of the MHTrack displayed on large video.
   */
  _videoTrack: Object,
};

/**
 * React {@code Component} responsible for displaying a label that indicates
 * the displayed video state of the current conference. {@code AudioOnlyLabel}
 * Will display when the conference is in audio only mode. {@code HDVideoLabel}
 * Will display if not in audio only mode and a high-definition large video is
 * being displayed.
 */
export class VideoQualityLabel extends AbstractVideoQualityLabel<Props> {
    /**
   * Implements React's {@link Component#render()}.
   *
   * @inheritdoc
   * @returns {ReactElement}
   */
    render() {
        const { _audioOnly, _labelKey, _tooltipKey, _videoTrack, t } = this.props;

        let className, labelContent, tooltipKey;

        if (_audioOnly) {
            className = 'audio-only label_buttons';
            labelContent = t('videoStatus.audioOnly');
            tooltipKey = 'videoStatus.labelTooltipAudioOnly';
        } else if (!_videoTrack || _videoTrack.muted) {
            className = 'no-video label_buttons';
            labelContent = t('videoStatus.audioOnly');
            tooltipKey = 'videoStatus.labelTooiltipNoVideo';
        } else {
            className = 'current-video-quality label_buttons';
            labelContent = t(_labelKey);
            tooltipKey = _tooltipKey;
        }

        return (
            <Tooltip
                content = { t(tooltipKey) }
                position = { 'bottom-right' }>
                <CircularLabel
                    className = { className }
                    id = 'videoResolutionLabel'
                    label = { labelContent } />
            </Tooltip>
        );
    }
}

/**
 * Maps (parts of) the Redux state to the associated {@code VideoQualityLabel}'s
 * props.
 *
 * @param {Object} state - The Redux state.
 * @private
 * @returns {{
 *     _labelKey: string,
 *     _tooltipKey: string,
 *     _videoTrack: Object
 * }}
 */
function _mapStateToProps(state) {
    const { enabled: audioOnly } = state['features/base/audio-only'];
    const { resolution, participantId } = state['features/large-video'];
    const config = state['features/base/config'];
    const videoTrackOnLargeVideo = getTrackByMediaTypeAndParticipant(
    state['features/base/tracks'],
    MEDIA_TYPE.VIDEO,
    participantId
    );

    // When the meeting is locked to a specific resolution via customResolution, the badge must reflect the
    // locked tier immediately - the WebRTC stats (which populate `features/large-video.resolution`) only
    // converge after the bridge / capture constraint has renegotiated, leaving a stale window where the
    // label would still show HD while the slider/lock notice say SD. The locked value is in the same units
    // as the bucketing function expects (frame height), so passing it directly lands in the right tier:
    // 240 -> LD, 360 -> SD, 480 -> SD, 720 -> HD.
    const isLockableContext = config?.interfaceConfig?.applyMeetingSettings === true && !config?.iAmRecorder;
    const lockedResolution
        = isLockableContext
            && typeof config?.customResolution === 'number'
            && Number.isInteger(config.customResolution)
            ? config.customResolution
            : undefined;
    const effectiveResolution = typeof lockedResolution === 'number'
        ? lockedResolution
        : resolution;

    const translationKeys = audioOnly
        ? {}
        : mapResolutionToTranslationsKeys(effectiveResolution);

    return {
        ..._abstractMapStateToProps(state),
        _labelKey: translationKeys.labelKey,
        _tooltipKey: translationKeys.tooltipKey,
        _videoTrack: videoTrackOnLargeVideo
    };
}

export default translate(connect(_mapStateToProps)(VideoQualityLabel));
